import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { Author, Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import {
  control,
  Control,
  divIcon,
  FitBoundsOptions,
  icon,
  LatLng,
  latLng,
  latLngBounds,
  LatLngBounds,
  Layer,
  LeafletMouseEvent,
  Map,
  MapOptions,
  Marker,
  marker,
  MarkerOptions,
  PointExpression,
  polyline,
  TileLayer,
  tileLayer,
} from 'leaflet';
import { MediaBlockService, SelectionCoordinate } from './media-block-service';
import { ProfileService } from './profile.service';
import { ColorService } from './color.service';
import { PlayerService } from '@kinomap-dev/ng-ui-lib/player';

@Injectable()
export class LeafletService {
  private readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);
  private readonly playerService: PlayerService = inject(PlayerService);
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly colorService: ColorService = inject(ColorService);

  private readonly defaultStartCoordinates: LatLng = latLng(50.367814743823594, 3.081704441274001);

  private readonly iconAnchor: PointExpression = [24, 24];
  private readonly iconSize: PointExpression = [48, 48];

  private osmTileLayer: TileLayer = new TileLayer(
    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  );

  private satelliteTileLayer: TileLayer = new TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  );

  private topoTileLayer: TileLayer = new TileLayer(
    'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
  );

  private baseMaps = {
    Satellite: this.satelliteTileLayer,
    Standard: this.osmTileLayer,
    Topographique: this.topoTileLayer,
  };

  private map: WritableSignal<Map | undefined> = signal(undefined);

  private baseLayerControl: Control.Layers | undefined;

  private polylineLayers: WritableSignal<Layer[]> = signal([]);
  private markerLayers: WritableSignal<Layer[]> = signal([]);
  private syncMarkerLayer: WritableSignal<Marker<any> | undefined> = signal(undefined);
  public layers = computed(() => [
    ...this.polylineLayers(),
    ...this.markerLayers(),
    ...(this.syncMarkerLayer() ? [this.syncMarkerLayer()!] : []),
  ]);

  public startPoint: WritableSignal<LatLng | undefined> = signal(undefined);
  public endPoint: WritableSignal<LatLng | undefined> = signal(undefined);
  public clientPoint: WritableSignal<LatLng | undefined> = signal(undefined);

  /** Point du tracé survolé sur le profil altimétrique, prioritaire sur la lecture. */
  public hoveredPoint: Signal<LatLng | undefined> = computed(() => {
    const coordinate: Coordinate | undefined = this.mediaBlockService.hoveredCoordinate();
    return coordinate ? latLng(coordinate.latitude, coordinate.longitude) : undefined;
  });

  private addBaseLayerControl(baseMaps: Record<string, TileLayer>): void {
    const map: Map | undefined = this.map();
    if (!map || this.baseLayerControl) return;
    this.baseLayerControl = control.layers(baseMaps).addTo(map);
  }

  private startMarker: MarkerOptions = {
    icon: icon({
      iconAnchor: this.iconAnchor,
      iconSize: this.iconSize,
      iconUrl: 'assets/storybook-assets/svg/map_marker_start.svg',
    }),
  };
  private endMarker: MarkerOptions = {
    icon: icon({
      iconAnchor: this.iconAnchor,
      iconSize: this.iconSize,
      iconUrl: 'assets/storybook-assets/svg/map_marker_end.svg',
    }),
  };

  private avatarUrl: Signal<string> = computed(
    () =>
      this.profileService.avatarUrl() ||
      `https://ui-avatars.com/api/?name=${this.profileService.profile().username}&background=097157&size=144&color=ffffff`,
  );

  /* le marker du client avec son avatar */
  private get clientMarker(): MarkerOptions {
    return {
      icon: divIcon({
        html: `<img src="${this.avatarUrl()}" alt="avatar" />`,
        iconAnchor: [16, 16],
        iconSize: [32, 32],
        className: 'sync-leaflet-div-icon',
      }),
    };
  }

  /** Le marker de l'avatar est conservé et déplacé : le survol du profil altimétrique
   * le repositionne à chaque mouvement de souris, et le recréer ferait clignoter son
   * DOM. Il n'est reconstruit que si l'avatar lui-même change. */
  private avatarMarker: Marker | undefined;
  private avatarMarkerUrl: string | undefined;

  private moveAvatarMarker(position: LatLng): Marker {
    const avatarUrl: string = this.avatarUrl();
    if (!this.avatarMarker || this.avatarMarkerUrl !== avatarUrl) {
      this.avatarMarker = marker(position, this.clientMarker);
      this.avatarMarkerUrl = avatarUrl;
    } else {
      this.avatarMarker.setLatLng(position);
    }
    return this.avatarMarker;
  }

  /* le marker de la syncro */
  private syncMarkerOption: MarkerOptions = {
    icon: icon({
      iconAnchor: [20, 40],
      iconSize: [40, 40],
      iconUrl: 'assets/images/pin-marker.svg',
    }),
  };

  // *** PUBLIC VARIABLES
  public fitBoundsOptions: WritableSignal<FitBoundsOptions> = signal({});
  public options: WritableSignal<MapOptions> = signal({
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 22,
        attribution: '...',
      }),
    ],
    zoom: 5,
    center: latLng(46.879966, -121.726909),
  });

  public fitBounds: WritableSignal<LatLngBounds> = signal<LatLngBounds>(
    latLngBounds(this.defaultStartCoordinates, this.defaultStartCoordinates),
  );

  getCoordinateFromTime(time: number): LatLng | undefined {
    const coordinates: Coordinate[] = this.mediaBlockService.syncedCoordinates();
    if (!coordinates || coordinates.length === 0) {
      return undefined;
    }
    const firstTime: number = coordinates[0].coo_time;
    const coordinate: Coordinate = coordinates.reduce((prev, curr) =>
      Math.abs(curr.coo_time - (firstTime + time)) < Math.abs(prev.coo_time - (firstTime + time))
        ? curr
        : prev,
    );
    return latLng(coordinate.latitude, coordinate.longitude);
  }

  onMapReady(mapInstance: Map): void {
    this.map.set(mapInstance);
    this.animateZoom(2);
  }

  addSyncMarker(latLng: LatLng): void {
    if (this.mediaBlockService.contentType !== 'scenic') return;
    if (!this.mediaBlockService.syncModeActive()) return;
    const coordinates: Coordinate[] = this.mediaBlockService.syncedCoordinates();
    if (!coordinates.length) return;

    const closest: Coordinate = coordinates.reduce((prev, curr) => {
      const currDist = Math.hypot(curr.latitude - latLng.lat, curr.longitude - latLng.lng);
      const prevDist = Math.hypot(prev.latitude - latLng.lat, prev.longitude - latLng.lng);
      return currDist < prevDist ? curr : prev;
    });
    const closestLatLng: LatLng = new LatLng(closest.latitude, closest.longitude);
    this.syncMarkerLayer.set(new Marker(closestLatLng, this.syncMarkerOption));
    this.mediaBlockService.syncMarkerCoordinate.set(closest);
  }

  private animateZoom(duration: number): Signal<boolean> {
    const done: WritableSignal<boolean> = signal<boolean>(false);
    const options: FitBoundsOptions = {
      duration: duration,
      maxZoom: 18,
      padding: [50, 50],
    };
    this.map()?.flyToBounds(this.fitBounds(), options);
    setTimeout(() => {
      done.set(true);
    }, duration * 1000);
    return done;
  }

  constructor() {
    effect(() => {
      this.options.set({ ...untracked(this.options), layers: [this.osmTileLayer] });
      this.addBaseLayerControl(this.baseMaps);
      this.updateMapData(this.mediaBlockService.syncedCoordinates());
    });

    // Le pin suit le service : il disparaît dès que la coordonnée de repère est vidée (synchro validée, reset…).
    effect(() => {
      if (!this.mediaBlockService.syncMarkerCoordinate()) this.syncMarkerLayer.set(undefined);
    });

    effect(() => {
      const selCoord = this.mediaBlockService.selectionCoordinate();
      if (!selCoord) return;
      this.startPoint.set(latLng(selCoord.start.latitude, selCoord.start.longitude));
      this.endPoint.set(latLng(selCoord.end.latitude, selCoord.end.longitude));
    });

    effect(() => {
      this.clientPoint.set(
        this.getCoordinateFromTime(
          this.playerService.progress() + this.mediaBlockService.syncOffsetSeconds(),
        ),
      );
    });

    effect(() => {
      const start: LatLng | undefined = this.startPoint();
      const end: LatLng | undefined = this.endPoint();
      const clientPosition: LatLng | undefined = this.clientPoint();
      const hoveredPosition: LatLng | undefined = this.hoveredPoint();
      const avatarPosition: LatLng | undefined =
        hoveredPosition ?? (this.playerService.progress() > 0 ? clientPosition : undefined);
      const markers: Layer[] = [];
      if (start) markers.push(marker(start, this.startMarker));
      if (end) markers.push(marker(end, this.endMarker));
      if (avatarPosition) markers.push(this.moveAvatarMarker(avatarPosition));
      this.markerLayers.set(markers);
    });
  }

  private addPolyline(
    coordinates: Coordinate[] | LatLng[],
    color: string,
    weight: number,
    opacity: number = 1,
  ): void {
    let coordinatesPolyline: LatLng[] = [];
    if (coordinates instanceof Array) {
      if (coordinates.length > 0 && 'latitude' in coordinates[0]) {
        coordinatesPolyline = (coordinates as Coordinate[]).map((coordinate) =>
          latLng(coordinate.latitude, coordinate.longitude),
        );
      } else {
        coordinatesPolyline = coordinates as LatLng[];
      }
    }

    const polylineLayer = polyline(coordinatesPolyline, {
      color: color,
      opacity: opacity,
      smoothFactor: 0.6,
      weight: weight,
    });

    this.polylineLayers.update((current) => [...current, polylineLayer]);
  }

  /**
   * Crée la polyline "Normal" du tracé utilisable,
   * un tracé blanc avec un contour noir (vert en mode synchro).
   * @param coordinates
   */
  private addMainPolyline(coordinates: Coordinate[]): void {
    this.addPolyline(coordinates, 'black', 8);
    this.addPolyline(
      coordinates,
      this.mediaBlockService.syncModeActive() ? this.colorService.success() : 'white',
      4,
    );
  }

  /**
   * Crée la polyline "fantôme" du tracé, c'est à dire toute la partie "non utilisable"
   * un tracé gris avec une légère opacité.
   * @param coordinates
   */
  private addFadedPolyline(coordinates: Coordinate[]): void {
    // En mode synchro : vert clair avec un contour vert.
    if (this.mediaBlockService.syncModeActive()) {
      this.addPolyline(coordinates, this.colorService.success(), 8);
      this.addPolyline(coordinates, this.colorService.success100(), 4);
      return;
    }
    this.addPolyline(coordinates, '#989A9D', 8, 0.8);
  }

  private updateMapData(coordinates: Coordinate[]): void {
    if (!coordinates || coordinates.length < 1) return;
    this.polylineLayers.set([]);
    const latlngs: LatLng[] = coordinates.map((c) => latLng(c.latitude, c.longitude));
    this.fitBounds.set(latLngBounds(latlngs));

    const selCoord: SelectionCoordinate | undefined = this.mediaBlockService.selectionCoordinate();
    if (!selCoord) {
      this.addMainPolyline(coordinates);
      return;
    }

    const startIdx: number = coordinates.findIndex((c) => c.coo_time >= selCoord.start.coo_time);
    const endIdx: number = coordinates.findIndex((c) => c.coo_time >= selCoord.end.coo_time);

    const before: Coordinate[] = coordinates.slice(0, startIdx + 1);
    const selection: Coordinate[] = coordinates.slice(startIdx, endIdx + 1);
    const after: Coordinate[] = coordinates.slice(endIdx);

    if (before.length > 1) this.addFadedPolyline(before);
    this.addMainPolyline(selection);
    if (after.length > 1) this.addFadedPolyline(after);
  }
}
